import { useState, useRef, useEffect, forwardRef, useImperativeHandle } from 'react';

const PlayIcon = () => (
    <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
        <path d="M8 5v14l11-7z"/>
    </svg>
);

const PauseIcon = () => (
    <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
        <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z"/>
    </svg>
);

const ExpandIcon = () => (
    <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
        <path d="M4 4h6V2H2v8h2V4zm16 0v6h2V2h-8v2h6zM4 20v-6H2v8h8v-2H4zm16 0h-6v2h8v-8h-2v6z"/>
    </svg>
);

const ContractIcon = () => (
    <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
        <path d="M4 4h6V2H2v8h2V4zm16 0v6h2V2h-8v2h6zM4 20v-6H2v8h8v-2H4zm16 0h-6v2h8v-8h-2v6z"/>
    </svg>
);

const formatTime = (seconds) => {
    if (!seconds || isNaN(seconds)) return '0:00';
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60);
    return `${m}:${s.toString().padStart(2, '0')}`;
};

const VideoPlayerCard = forwardRef(({ src, poster, title }, ref) => {
    const [playing, setPlaying] = useState(false);
    const [progress, setProgress] = useState(0);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [showControls, setShowControls] = useState(true);
    const cardRef = useRef(null);
    const videoRef = useRef(null);
    const controlsTimerRef = useRef(null);

    useImperativeHandle(ref, () => ({
        stopVideo: () => {
            if (videoRef.current) {
                videoRef.current.pause();
                videoRef.current.currentTime = 0;
                setPlaying(false);
                setProgress(0);
                setCurrentTime(0);
            }
        }
    }));

    const handlePlay = () => {
        if (videoRef.current) {
            videoRef.current.play();
            setPlaying(true);
        }
    };

    const handleTimeUpdate = () => {
        if (videoRef.current) {
            const { currentTime: ct, duration: d } = videoRef.current;
            if (d > 0) {
                setProgress((ct / d) * 100);
                setCurrentTime(ct);
                setDuration(d);
            }
        }
    };

    const handleEnded = () => {
        setPlaying(false);
        setProgress(0);
        setCurrentTime(0);
        if (videoRef.current) {
            videoRef.current.currentTime = 0;
        }
    };

    const handleFullscreen = () => {
        if (!document.fullscreenElement) {
            cardRef.current.requestFullscreen?.();
            setIsFullscreen(true);
        } else {
            document.exitFullscreen?.();
            setIsFullscreen(false);
        }
    };

    const handleSeek = (e) => {
        const rect = e.currentTarget.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const pct = x / rect.width;
        if (videoRef.current && duration > 0) {
            videoRef.current.currentTime = pct * duration;
            setProgress(pct * 100);
        }
    };

    const handleMouseMove = () => {
        setShowControls(true);
        if (controlsTimerRef.current) clearTimeout(controlsTimerRef.current);
        if (playing) {
            controlsTimerRef.current = setTimeout(() => setShowControls(false), 3000);
        }
    };

    return (
        <div
            ref={cardRef}
            className="video-player-card"
            onClick={!playing ? handlePlay : undefined}
            onMouseMove={handleMouseMove}
            onMouseLeave={() => playing && setShowControls(false)}
        >
            <video
                ref={videoRef}
                src={src}
                poster={poster}
                onTimeUpdate={handleTimeUpdate}
                onEnded={handleEnded}
                onClick={(e) => {
                    e.stopPropagation();
                    if (videoRef.current) {
                        if (videoRef.current.paused) {
                            videoRef.current.play();
                            setPlaying(true);
                        } else {
                            videoRef.current.pause();
                            setPlaying(false);
                            setShowControls(true);
                        }
                    }
                }}
            />

            {/* 品牌标识 */}
            <div className="video-brand-badge">
                <span>XIAOMAI</span>
            </div>

            {/* 封面播放层 */}
            <div className={`video-cover ${playing ? 'hidden' : ''}`}>
                <div className="video-play-btn">
                    <PlayIcon />
                </div>
            </div>

            {/* 标题 */}
            <div className="video-title-overlay">
                <span>{title}</span>
            </div>

            {/* 自定义控制栏 */}
            <div className={`video-controls ${showControls ? 'visible' : ''}`} onClick={(e) => e.stopPropagation()}>
                {/* 进度条 */}
                <div className="video-seek-bar" onClick={handleSeek}>
                    <div className="video-seek-track">
                        <div className="video-seek-fill" style={{ width: `${progress}%` }} />
                        <div className="video-seek-thumb" style={{ left: `${progress}%` }} />
                    </div>
                </div>

                {/* 底部按钮行 */}
                <div className="video-controls-row">
                    <div className="video-controls-left">
                        <button
                            className="video-ctrl-btn"
                            onClick={() => {
                                if (videoRef.current) {
                                    if (videoRef.current.paused) {
                                        videoRef.current.play();
                                        setPlaying(true);
                                    } else {
                                        videoRef.current.pause();
                                        setPlaying(false);
                                    }
                                }
                            }}
                        >
                            {playing ? <PauseIcon /> : <PlayIcon />}
                        </button>
                        <span className="video-time">
                            {formatTime(currentTime)} / {formatTime(duration)}
                        </span>
                    </div>
                    <button className="video-ctrl-btn" onClick={handleFullscreen}>
                        {isFullscreen ? <ContractIcon /> : <ExpandIcon />}
                    </button>
                </div>
            </div>
        </div>
    );
});

export default VideoPlayerCard;
